import { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { Link, useLocation, useNavigate } from "react-router-dom";
import styled, { keyframes } from "styled-components";
import Navbar from "../../components/Navbar";
import NoteCard from "../../components/NoteCard";
import { publicRequest } from "../../requestMethods";
import NoteCardSkeleton from "../../components/NoteCardSkeleton";
import FolderCardSkeleton from "../../components/FolderCardSkeleton";
import theme from "../../theme";
import { mobile } from "../../responsive";
import { IconFolder, IconPlus, IconDocument, IconUsers } from "../../icons";
import ActivityWidget from "../../components/ActivityWidget";
import communityImg from "../../assets/community.png";
import ltceLogo from "../../assets/ltce.png";

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const bump = keyframes`
  0%   { transform: scale(1); }
  40%  { transform: scale(0.88); }
  70%  { transform: scale(1.08); }
  100% { transform: scale(1); }
`;

/* ── Shell ── */
const Shell = styled.div`
  display: flex;
  min-height: 100vh;
  background: ${theme.bg};
`;

/* ── Sidebar ── */
const Sidebar = styled.aside`
  width: ${theme.sidebarWidth};
  flex-shrink: 0;
  background: ${theme.sidebarBg};
  border-right: 1px solid ${theme.separator};
  display: flex;
  flex-direction: column;
  position: fixed;
  top: 0; left: 0;
  height: 100vh;
  z-index: 60;
  ${mobile(`display: none;`)}
`;

const SidebarTop = styled.div`
  padding: 22px 18px 16px;
  border-bottom: 1px solid ${theme.separator};
`;

const LogoRow = styled(Link)`
  display: flex; align-items: center; gap: 10px; text-decoration: none;
`;

const LogoMark = styled.div`
  width: 36px; height: 36px; background: ${theme.primary}; color: #fff;
  border-radius: 9px; display: flex; align-items: center; justify-content: center;
  font-size: 17px; font-weight: 800; font-family: Georgia, serif; flex-shrink: 0;
`;

const LogoText = styled.div`font-size: 14px; font-weight: 700; color: ${theme.textMain}; line-height: 1.2;`;

const Nav = styled.nav`
  padding: 14px 10px 6px; display: flex; flex-direction: column; gap: 2px;
`;

const SidebarMiddle = styled.div`
  flex: 1; display: flex; flex-direction: column; justify-content: center;
  overflow: hidden; min-height: 0;
`;

const NavItem = styled(Link)`
  display: flex; align-items: center; gap: 10px; padding: 9px 12px; border-radius: 8px;
  font-size: 13px; font-weight: ${(p) => (p.$active ? "700" : "500")};
  color: ${(p) => (p.$active ? theme.primary : theme.textMuted)};
  background: ${(p) => (p.$active ? theme.primaryLight : "transparent")};
  text-decoration: none; transition: background 0.15s, color 0.15s;
  &:hover { background: ${theme.primaryLight}; color: ${theme.primary}; }
  &:active { animation: ${bump} 0.3s ease; }
`;

const CommunityMainNavItem = styled(NavItem)`justify-content: flex-start;`;

const CommunityChevron = styled.span`
  width: 7px;
  height: 7px;
  display: inline-block;
  border-right: 1.8px solid currentColor;
  border-bottom: 1.8px solid currentColor;
  transform: ${(p) => (p.$open ? "rotate(-135deg)" : "rotate(45deg)")};
  margin-top: ${(p) => (p.$open ? "2px" : "-1px")};
  transition: transform 0.15s ease, margin-top 0.15s ease;
`;

const CommunityChevronHit = styled.span`
  margin-left: auto;
  width: 24px;
  height: 24px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s;
  &:hover { background: ${theme.primaryLight}; }
`;

const CommunitySubList = styled.div`
  margin: 4px 0 4px 8px;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const CommunitySubItem = styled.button`
  width: 100%;
  border: none;
  border-radius: 8px;
  background: ${(p) => (p.$active ? theme.primaryLight : "transparent")};
  color: ${(p) => (p.$active ? theme.primary : theme.textMuted)};
  padding: 7px 10px;
  font-size: 12px;
  font-weight: ${(p) => (p.$active ? "700" : "600")};
  font-family: inherit;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  text-align: left;
  transition: background 0.15s, color 0.15s;
  &:hover { background: ${theme.primaryLight}; color: ${theme.primary}; }
`;

const CommunitySubAvatar = styled.img`
  width: 18px;
  height: 18px;
  border-radius: 5px;
  object-fit: cover;
  flex-shrink: 0;
`;

const SidebarUser = styled.div`
  padding: 14px 16px; border-top: 1px solid ${theme.separator};
  display: flex; align-items: center; gap: 10px;
`;

const UserAvatar = styled.div`
  width: 34px; height: 34px; border-radius: 50%; background: ${theme.bgAlt};
  border: 1.5px solid ${theme.cardBorder}; overflow: hidden; flex-shrink: 0;
  display: flex; align-items: center; justify-content: center;
  font-size: 13px; font-weight: 700; color: ${theme.primary}; cursor: pointer;
`;

const UserAvatarImg = styled.img`width: 100%; height: 100%; object-fit: cover;`;
const UserName = styled.div`font-size: 12.5px; font-weight: 700; color: ${theme.textMain}; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;`;
const UserRole = styled.div`font-size: 10px; font-weight: 600; color: ${theme.textMuted}; text-transform: uppercase; letter-spacing: 0.06em;`;

/* ── Main ── */
const Main = styled.div`
  margin-left: ${theme.sidebarWidth};
  flex: 1; display: flex; flex-direction: column; min-width: 0;
  ${mobile(`margin-left: 0;`)}
`;

const Content = styled.main`
  padding: 32px 32px 80px;
  font-family: Verdana, Geneva, sans-serif;
  ${mobile(`padding: 20px 14px 80px;`)}
`;

const TopRow = styled.div`
  display: flex; align-items: center; justify-content: space-between;
  margin-bottom: 28px; flex-wrap: wrap; gap: 12px;
`;

const PageTitle = styled.h1`
  font-size: 22px; font-weight: 800; color: ${theme.textMain}; margin: 0;
  display: flex; align-items: center; gap: 10px; letter-spacing: -0.3px;
`;

const PrimaryButton = styled.button`
  background: ${theme.primary}; color: #fff; border: none; border-radius: 8px;
  padding: 9px 20px; font-size: 13px; font-weight: 700; font-family: inherit;
  cursor: pointer; display: flex; align-items: center; gap: 7px;
  transition: background 0.18s, transform 0.1s;
  &:hover { background: ${theme.primaryDark}; transform: translateY(-1px); }
  &:disabled { opacity: 0.55; cursor: not-allowed; }
  &:active:not(:disabled) { animation: ${bump} 0.3s ease; }
`;

const FolderGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(230px, 1fr));
  gap: 16px; margin-bottom: 32px;
`;

const FolderCard = styled.div`
  background: ${theme.cardBg};
  border: 1.5px solid ${(p) => (p.$active ? theme.primary : theme.cardBorder)};
  border-radius: 12px; padding: 20px 18px 16px; cursor: pointer;
  display: flex; flex-direction: column; gap: 8px;
  box-shadow: 0 1px 6px rgba(26, 18, 8, 0.05);
  transition: border-color 0.15s, transform 0.15s, box-shadow 0.15s;
  animation: ${fadeUp} 0.3s ease both;
  &:hover { border-color: ${theme.primary}; transform: translateY(-2px); box-shadow: 0 6px 20px rgba(26, 18, 8, 0.1); }
`;

const FolderTopRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 2px;
`;

const FolderIconWrap = styled.div`color: ${theme.primary}; display: flex; align-items: center;`;
const FolderName = styled.span`font-size: 13px; font-weight: 700; color: ${theme.textMain}; word-break: break-word;`;
const FolderCount = styled.span`font-size: 11px; color: ${theme.textMuted};`;

const MemberStack = styled.div`
  display: flex;
  align-items: center;
  margin-left: auto;
`;

const MemberAvatar = styled.div`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  border: 2px solid ${theme.cardBg};
  background: ${theme.bgAlt};
  color: ${theme.primary};
  font-size: 10px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-left: -8px;
  overflow: hidden;

  &:first-child {
    margin-left: 0;
  }
`;

const MemberAvatarImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const MemberMore = styled(MemberAvatar)`
  background: #f4d8bb;
  color: ${theme.textMain};
  font-size: 10px;
`;

const FolderActions = styled.div`display: flex; gap: 5px; margin-top: 4px; flex-wrap: wrap;`;

const SmallButton = styled.button`
  font-size: 11px; font-weight: 600; padding: 3px 10px; border-radius: 5px;
  cursor: pointer; border: 1.5px solid ${theme.cardBorder}; background: transparent;
  color: ${theme.textMuted}; font-family: inherit;
  transition: color 0.15s, border-color 0.15s, background 0.15s;
  &:hover {
    color: ${(p) => (p.$danger ? theme.danger : theme.primary)};
    border-color: ${(p) => (p.$danger ? theme.danger : theme.primary)};
    background: ${(p) => (p.$danger ? theme.dangerLight : theme.primaryLight)};
  }
  &:active { animation: ${bump} 0.3s ease; }
`;

const FolderDetail = styled.div`
  background: ${theme.cardBg}; border: 1.5px solid ${theme.cardBorder};
  border-radius: 14px; padding: 24px; box-shadow: 0 1px 6px rgba(26, 18, 8, 0.05);
`;

const DetailHeader = styled.div`
  display: flex; align-items: center; gap: 12px; margin-bottom: 20px; flex-wrap: wrap;
`;

const BackButton = styled.button`
  background: transparent; border: 1.5px solid ${theme.cardBorder}; border-radius: 6px;
  padding: 6px 14px; font-size: 12px; font-weight: 700; font-family: inherit;
  color: ${theme.textMuted}; cursor: pointer; transition: border-color 0.15s, color 0.15s;
  &:hover { border-color: ${theme.primary}; color: ${theme.primary}; }
  &:active { animation: ${bump} 0.3s ease; }
`;

const DetailTitle = styled.h2`
  font-size: 16px; font-weight: 700; color: ${theme.textMain}; margin: 0; flex: 1;
  display: flex; align-items: center; gap: 8px;
`;

const NotesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(320px, 1fr));
  gap: 16px;
  width: 100%;
  align-items: start;
  ${mobile(`grid-template-columns: 1fr;`)}

  @media (max-width: 1100px) {
    grid-template-columns: 1fr;
  }
`;

const EmptyState = styled.p`font-size: 13px; color: ${theme.textMuted}; text-align: center; padding: 32px 0;`;
const StatusText = styled.p`font-size: 13px; color: ${(p) => (p.$error ? theme.danger : theme.textMuted)}; text-align: center; padding: 24px 0;`;

const Overlay = styled.div`
  position: fixed; inset: 0; background: rgba(26, 18, 8, 0.4); backdrop-filter: blur(3px);
  display: flex; align-items: center; justify-content: center; z-index: 200; padding: 20px;
`;

const Modal = styled.div`
  background: ${theme.cardBg}; border-radius: 14px; border: 1.5px solid ${theme.cardBorder};
  padding: 26px 26px 22px; width: 100%; max-width: 380px;
  box-shadow: 0 16px 48px rgba(26, 18, 8, 0.15); animation: ${fadeUp} 0.25s ease both;
`;

const ModalTitle = styled.h3`font-size: 15px; font-weight: 700; color: ${theme.textMain}; margin: 0 0 14px;`;

const ModalInput = styled.input`
  width: 100%; background: ${theme.bg}; border: 1.5px solid ${theme.cardBorder};
  border-radius: 7px; padding: 9px 12px; font-size: 13px; font-family: inherit;
  color: ${theme.textMain}; outline: none; box-sizing: border-box; margin-bottom: 14px;
  &:focus { border-color: ${theme.primary}; box-shadow: 0 0 0 3px ${theme.primaryGlow}; }
`;

const ModalRow = styled.div`display: flex; gap: 10px; justify-content: flex-end;`;

const CancelButton = styled.button`
  background: transparent; border: 1.5px solid ${theme.cardBorder}; border-radius: 6px;
  padding: 7px 18px; font-size: 12px; font-weight: 600; font-family: inherit;
  color: ${theme.textMuted}; cursor: pointer;
  &:hover { border-color: ${theme.textMuted}; }
`;

const NoteCardWithRemove = styled.div`
  border-radius: 14px;
  border: 1.5px solid ${theme.cardBorder};
  overflow: hidden;
  background: ${theme.cardBg};
  box-shadow: 0 1px 6px rgba(26, 18, 8, 0.05);
  width: 100%;

  /* strip the card's own border/shadow since we wrap it */
  & > div > div:first-child {
    border: none;
    box-shadow: none;
    border-radius: 0;
  }
`;

const RemoveStrip = styled.button`
  width: 100%;
  padding: 9px 16px;
  border: none;
  border-top: 1.5px solid ${theme.separator};
  background: transparent;
  color: ${theme.danger};
  font-size: 12px;
  font-weight: 700;
  font-family: inherit;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  transition: background 0.15s;
  &:hover { background: ${theme.dangerLight}; }
  &:active { animation: ${bump} 0.3s ease; }
`;

const FolderOption = styled.div`
  display: flex; align-items: center; justify-content: space-between;
  padding: 10px 12px; border-radius: 7px; border: 1.5px solid ${theme.cardBorder};
  margin-bottom: 8px; cursor: pointer; transition: border-color 0.15s, background 0.15s;
  &:hover { border-color: ${theme.primary}; background: ${theme.primaryLight}; }
`;

export default function Folders() {
  const currentUser = useSelector((s) => s.user.currentUser);
  const location = useLocation();
  const navigate = useNavigate();
  const [folders, setFolders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeFolder, setActiveFolder] = useState(null);
  const [folderLoading, setFolderLoading] = useState(false);
  const [modal, setModal] = useState(null);
  const [modalInput, setModalInput] = useState("");
  const [modalSubmitting, setModalSubmitting] = useState(false);
  const [addNoteModal, setAddNoteModal] = useState(null);
  const [userNotes, setUserNotes] = useState([]);
  const [userNotesLoading, setUserNotesLoading] = useState(false);
  const [shareModal, setShareModal] = useState(null);
  const [shareUserQuery, setShareUserQuery] = useState("");
  const [shareRole, setShareRole] = useState("viewer");
  const [shareCandidates, setShareCandidates] = useState([]);
  const [shareLoading, setShareLoading] = useState(false);
  const [sharingSubmitting, setSharingSubmitting] = useState(false);
  const [collaborators, setCollaborators] = useState([]);
  const [pendingInvites, setPendingInvites] = useState([]);
  const [communities, setCommunities] = useState([]);
  const [myCommunity, setMyCommunity] = useState(null);
  const [communityMenuOpen, setCommunityMenuOpen] = useState(false);

  const STATIC_URL = (process.env.REACT_APP_BASE_URL || "http://localhost:8000/api").replace("/api", "");
  const avatarSrc = currentUser?.profilePicture ? `${STATIC_URL}/images/${currentUser.profilePicture}` : null;

  useEffect(() => {
    if (!currentUser) return;
    setLoading(true);
    publicRequest.get(`/folders/${currentUser._id}`)
      .then((res) => setFolders(res.data))
      .catch(() => setError("Failed to load folders."))
      .finally(() => setLoading(false));
  }, [currentUser]);

  useEffect(() => {
    const run = async () => {
      try {
        const r = await publicRequest.get("/community");
        setCommunities(r.data || []);
      } catch {
        setCommunities([]);
      }
    };
    run();
  }, []);

  useEffect(() => {
    if (!currentUser?.institution) return;
    publicRequest.get(`/community/institution/${encodeURIComponent(currentUser.institution)}`)
      .then((r) => {
        setMyCommunity(r.data);
        setCommunities((prev) => prev.find((c) => c._id === r.data._id) ? prev : [r.data, ...prev]);
      })
      .catch(() => {});
  }, [currentUser]);

  const openFolder = async (folder) => {
    setFolderLoading(true);
    try { const res = await publicRequest.get(`/folders/${folder._id}/notes`, { params: { userId: currentUser._id } }); setActiveFolder(res.data); }
    catch { setError("Failed to load folder contents."); }
    finally { setFolderLoading(false); }
  };

  const handleCreateFolder = async () => {
    if (!modalInput.trim()) return;
    setModalSubmitting(true);
    try {
      const res = await publicRequest.post("/folders", { userId: currentUser._id, name: modalInput.trim() });
      setFolders((p) => [res.data, ...p]); setModal(null); setModalInput("");
    } catch { setError("Failed to create folder."); }
    finally { setModalSubmitting(false); }
  };

  const handleRenameFolder = async () => {
    if (!modalInput.trim()) return;
    setModalSubmitting(true);
    try {
      const res = await publicRequest.put(`/folders/${modal.folder._id}/rename`, { userId: currentUser._id, name: modalInput.trim() });
      setFolders((p) => p.map((f) => (f._id === res.data._id ? res.data : f)));
      if (activeFolder?.folder?._id === res.data._id) setActiveFolder((p) => ({ ...p, folder: res.data }));
      setModal(null); setModalInput("");
    } catch { setError("Failed to rename folder."); }
    finally { setModalSubmitting(false); }
  };

  const handleDeleteFolder = async (folder) => {
    if (!window.confirm(`Delete folder "${folder.name}"?`)) return;
    try {
      await publicRequest.delete(`/folders/${folder._id}`, { data: { userId: currentUser._id } });
      setFolders((p) => p.filter((f) => f._id !== folder._id));
      if (activeFolder?.folder?._id === folder._id) setActiveFolder(null);
    } catch { setError("Failed to delete folder."); }
  };

  const handleRemoveNote = async (folderId, noteId) => {
    try {
      await publicRequest.put(`/folders/${folderId}/remove`, { userId: currentUser._id, noteId });
      setActiveFolder((p) => ({ ...p, notes: p.notes.filter((n) => n._id !== noteId), folder: { ...p.folder, noteIds: p.folder.noteIds.filter((id) => id !== noteId) } }));
      setFolders((p) => p.map((f) => f._id === folderId ? { ...f, noteIds: f.noteIds.filter((id) => id !== noteId) } : f));
    } catch { setError("Failed to remove note."); }
  };

  const openAddNoteModal = async (folder) => {
    setAddNoteModal(folder); setUserNotesLoading(true);
    try { const res = await publicRequest.get(`/notes/profile/${currentUser._id}`); setUserNotes(res.data); }
    catch { setUserNotes([]); }
    finally { setUserNotesLoading(false); }
  };

  const handleAddNote = async (noteId) => {
    try {
      const res = await publicRequest.put(`/folders/${addNoteModal._id}/add`, { userId: currentUser._id, noteId });
      setFolders((p) => p.map((f) => (f._id === res.data._id ? res.data : f)));
      if (activeFolder?.folder?._id === addNoteModal._id) {
        const note = userNotes.find((n) => n._id === noteId);
        if (note && !activeFolder.notes.find((n) => n._id === noteId)) setActiveFolder((p) => ({ ...p, notes: [note, ...p.notes] }));
      }
      setAddNoteModal(null);
    } catch { setError("Failed to add note to folder."); }
  };

  const openShareModal = async (folder) => {
    setShareModal(folder);
    setShareUserQuery("");
    setShareRole("viewer");
    setShareCandidates([]);
    setCollaborators([]);
    setPendingInvites([]);
    setShareLoading(true);
    try {
      const res = await publicRequest.get(`/folders/${folder._id}/collaborators`, {
        params: { userId: currentUser._id },
      });
      setCollaborators(res.data.collaborators || []);
      setPendingInvites(res.data.pendingInvites || []);
    } catch {
      setError("Failed to load sharing settings.");
    } finally {
      setShareLoading(false);
    }
  };

  const findUsersToShare = async () => {
    if (!shareUserQuery.trim()) {
      setShareCandidates([]);
      return;
    }
    setShareLoading(true);
    try {
      const res = await publicRequest.get(`/users/findusers/${encodeURIComponent(shareUserQuery.trim())}`);
      setShareCandidates(
        (res.data || []).filter(
          (u) => u._id !== currentUser._id && !(collaborators || []).find((c) => c.userId === u._id)
        )
      );
    } catch {
      setShareCandidates([]);
      setError("Failed to search users.");
    } finally {
      setShareLoading(false);
    }
  };

  const handleShareFolder = async (targetUserId) => {
    if (!shareModal) return;
    setSharingSubmitting(true);
    try {
      await publicRequest.put(`/folders/${shareModal._id}/share`, {
        userId: currentUser._id,
        targetUserId,
        role: shareRole,
      });
      const refresh = await publicRequest.get(`/folders/${shareModal._id}/collaborators`, {
        params: { userId: currentUser._id },
      });
      setCollaborators(refresh.data.collaborators || []);
      setPendingInvites(refresh.data.pendingInvites || []);
      setShareCandidates((p) => p.filter((u) => u._id !== targetUserId));
    } catch {
      setError("Failed to send invitation.");
    } finally {
      setSharingSubmitting(false);
    }
  };

  const handleRemoveCollaborator = async (targetUserId) => {
    if (!shareModal) return;
    setSharingSubmitting(true);
    try {
      const res = await publicRequest.delete(`/folders/${shareModal._id}/share/${targetUserId}`, {
        data: { userId: currentUser._id },
      });
      setCollaborators(res.data.collaborators || []);
    } catch {
      setError("Failed to remove access.");
    } finally {
      setSharingSubmitting(false);
    }
  };

  const isMember = (community) => community?.members?.some((m) => m === currentUser?._id || m?._id === currentUser?._id || m?.toString() === currentUser?._id);
  const isLTCECommunity = (community) => {
    const target = `${community?.name || ""} ${community?.institution || ""}`.toUpperCase();
    return target.includes("LTCE");
  };
  const getCommunityLogo = (community) => isLTCECommunity(community) ? ltceLogo : communityImg;
  const communityNavList = (() => {
    const items = [];
    if (myCommunity) items.push(myCommunity);
    communities.forEach((c) => {
      if (isMember(c) && !items.find((x) => x._id === c._id)) items.push(c);
    });
    return items;
  })();

  return (
    <Shell>
      {/* Sidebar */}
      <Sidebar>
        <SidebarTop>
          <LogoRow to="/">
            <LogoMark>N</LogoMark>
            <div><LogoText>NotesHub</LogoText></div>
          </LogoRow>
        </SidebarTop>
        <Nav>
          <NavItem to="/" $active={location.pathname === "/"}>
            <IconDocument size="15px" /> Feed
          </NavItem>
          <NavItem to="/folders" $active={location.pathname === "/folders"}>
            <IconFolder size="15px" /> Folders
          </NavItem>
          <CommunityMainNavItem to="/community" $active={location.pathname === "/community"}>
            <IconUsers size="15px" /> Community
            <CommunityChevronHit
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setCommunityMenuOpen((v) => !v);
              }}
              aria-label="Toggle my communities"
              title="Toggle my communities"
            >
              <CommunityChevron $open={communityMenuOpen} />
            </CommunityChevronHit>
          </CommunityMainNavItem>
          {communityMenuOpen && (
            <CommunitySubList>
              {communityNavList.length === 0 ? (
                <CommunitySubItem type="button" disabled style={{ opacity: 0.6, cursor: "default" }}>
                  No communities yet
                </CommunitySubItem>
              ) : communityNavList.map((c) => (
                <CommunitySubItem key={c._id} type="button" onClick={() => navigate("/community")}>
                  <CommunitySubAvatar src={getCommunityLogo(c)} alt={`Logo - ${c.institution || c.name}`} />
                  {c.name}
                </CommunitySubItem>
              ))}
            </CommunitySubList>
          )}
        </Nav>
        <SidebarMiddle>
          <ActivityWidget />
        </SidebarMiddle>
        {currentUser && (
          <SidebarUser>
            <UserAvatar onClick={() => navigate(`/profile/${currentUser._id}`)}>
              {avatarSrc ? <UserAvatarImg src={avatarSrc} alt={currentUser.username} /> : currentUser.username?.[0]?.toUpperCase()}
            </UserAvatar>
            <div style={{ flex: 1, minWidth: 0 }}>
              <UserName>{currentUser.username}</UserName>
              <UserRole>Student</UserRole>
            </div>
          </SidebarUser>
        )}
      </Sidebar>

      {/* Main */}
      <Main>
        <Navbar />
        <Content>
          <TopRow>
            <PageTitle><IconFolder size="20px" /> My Folders</PageTitle>
            <PrimaryButton onClick={() => { setModal("create"); setModalInput(""); }}>
              <IconPlus size="13px" /> New Folder
            </PrimaryButton>
          </TopRow>

          {error && <StatusText $error>{error}</StatusText>}

          {!activeFolder ? (
            loading ? (
              <FolderGrid>
                {Array.from({ length: 6 }).map((_, i) => <FolderCardSkeleton key={i} />)}
              </FolderGrid>
            )
            : folders.length === 0 ? <EmptyState>No folders yet. Create one to organise your notes.</EmptyState>
            : (
              <FolderGrid>
                {folders.map((folder) => (
                  <FolderCard key={folder._id} onClick={() => openFolder(folder)}>
                    {(() => {
                      const uniqueCount = typeof folder.noteCount === "number"
                        ? folder.noteCount
                        : new Set((folder.noteIds || []).map((id) => String(id))).size;
                      const members = folder.memberPreview || [];
                      const overflowCount = Math.max((folder.memberCount || members.length) - 3, 0);
                      return (
                        <>
                    <FolderTopRow>
                      <FolderIconWrap><IconFolder size="28px" /></FolderIconWrap>
                      {members.length > 0 && (
                        <MemberStack>
                          {members.map((m) => (
                            <MemberAvatar key={m.userId} title={m.username || "User"}>
                              {m.profilePicture
                                ? <MemberAvatarImg src={`${STATIC_URL}/images/${m.profilePicture}`} alt={m.username || "User"} />
                                : (m.username?.[0] || "U").toUpperCase()}
                            </MemberAvatar>
                          ))}
                          {overflowCount > 0 && <MemberMore>+{overflowCount}</MemberMore>}
                        </MemberStack>
                      )}
                    </FolderTopRow>
                    <FolderName>{folder.name}</FolderName>
                    <FolderCount>
                      {folder.accessRole === "owner"
                        ? "Owner"
                        : folder.accessRole === "editor"
                          ? "Editor"
                          : "Viewer"}
                    </FolderCount>
                    <FolderCount>{uniqueCount} note{uniqueCount !== 1 ? "s" : ""}</FolderCount>
                    <FolderActions onClick={(e) => e.stopPropagation()}>
                      {folder.accessRole === "owner" && (
                        <SmallButton onClick={() => { setModal({ type: "rename", folder }); setModalInput(folder.name); }}>Rename</SmallButton>
                      )}
                      {(folder.accessRole === "owner" || folder.accessRole === "editor") && (
                        <SmallButton onClick={() => openAddNoteModal(folder)}>+ Note</SmallButton>
                      )}
                      {folder.accessRole === "owner" && (
                        <SmallButton onClick={() => openShareModal(folder)}>Share</SmallButton>
                      )}
                      {folder.accessRole === "owner" && (
                        <SmallButton $danger onClick={() => handleDeleteFolder(folder)}>Delete</SmallButton>
                      )}
                    </FolderActions>
                        </>
                      );
                    })()}
                  </FolderCard>
                ))}
              </FolderGrid>
            )
          ) : (
            <FolderDetail>
              <DetailHeader>
                <BackButton onClick={() => setActiveFolder(null)}>← Back</BackButton>
                <DetailTitle><IconFolder size="16px" /> {activeFolder.folder.name}</DetailTitle>
                {(activeFolder.folder.accessRole === "owner" || activeFolder.folder.accessRole === "editor") && (
                  <SmallButton onClick={() => openAddNoteModal(activeFolder.folder)}>+ Add Note</SmallButton>
                )}
              </DetailHeader>
              {folderLoading ? (
                <NotesGrid>
                  {Array.from({ length: 4 }).map((_, i) => <NoteCardSkeleton key={i} />)}
                </NotesGrid>
              )
                : activeFolder.notes.length === 0 ? <EmptyState>This folder is empty.</EmptyState>
                : (
                  <NotesGrid>
                    {activeFolder.notes.map((note) => (
                      <NoteCardWithRemove key={note._id}>
                        <NoteCard note={note} readOnly />
                        {(activeFolder.folder.accessRole === "owner" || activeFolder.folder.accessRole === "editor") && (
                          <RemoveStrip onClick={() => handleRemoveNote(activeFolder.folder._id, note._id)}>
                            ✕ Remove from folder
                          </RemoveStrip>
                        )}
                      </NoteCardWithRemove>
                    ))}
                  </NotesGrid>
                )}
            </FolderDetail>
          )}
        </Content>
      </Main>

      {/* Create / Rename modal */}
      {modal && (
        <Overlay onClick={() => setModal(null)}>
          <Modal onClick={(e) => e.stopPropagation()}>
            <ModalTitle>{modal === "create" ? "New Folder" : "Rename Folder"}</ModalTitle>
            <ModalInput autoFocus placeholder="Folder name" value={modalInput}
              onChange={(e) => setModalInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") modal === "create" ? handleCreateFolder() : handleRenameFolder(); if (e.key === "Escape") setModal(null); }}
            />
            <ModalRow>
              <CancelButton onClick={() => setModal(null)}>Cancel</CancelButton>
              <PrimaryButton disabled={modalSubmitting || !modalInput.trim()} onClick={modal === "create" ? handleCreateFolder : handleRenameFolder}>
                {modalSubmitting ? "Saving…" : modal === "create" ? "Create" : "Rename"}
              </PrimaryButton>
            </ModalRow>
          </Modal>
        </Overlay>
      )}

      {/* Add note modal */}
      {addNoteModal && (
        <Overlay onClick={() => setAddNoteModal(null)}>
          <Modal onClick={(e) => e.stopPropagation()}>
            <ModalTitle>Add note to "{addNoteModal.name}"</ModalTitle>
            {userNotesLoading ? <StatusText>Loading…</StatusText>
              : userNotes.length === 0 ? <EmptyState>No notes to add.</EmptyState>
              : userNotes.map((note) => {
                const alreadyIn = addNoteModal.noteIds.includes(note._id);
                return (
                  <FolderOption key={note._id} onClick={() => !alreadyIn && handleAddNote(note._id)} style={{ opacity: alreadyIn ? 0.5 : 1, cursor: alreadyIn ? "default" : "pointer" }}>
                    <span style={{ fontSize: 12, fontWeight: 700, color: theme.textMain }}>{note.notename}</span>
                    {alreadyIn ? <span style={{ fontSize: 11, color: theme.textMuted }}>Added</span> : <span style={{ fontSize: 11, color: theme.primary, fontWeight: 700 }}>+ Add</span>}
                  </FolderOption>
                );
              })}
            <ModalRow><CancelButton onClick={() => setAddNoteModal(null)}>Close</CancelButton></ModalRow>
          </Modal>
        </Overlay>
      )}

      {/* Share folder modal */}
      {shareModal && (
        <Overlay onClick={() => setShareModal(null)}>
          <Modal onClick={(e) => e.stopPropagation()}>
            <ModalTitle>Share "{shareModal.name}"</ModalTitle>

            <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
              <ModalInput
                style={{ marginBottom: 0 }}
                placeholder="Search username"
                value={shareUserQuery}
                onChange={(e) => setShareUserQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") findUsersToShare();
                }}
              />
              <select
                value={shareRole}
                onChange={(e) => setShareRole(e.target.value)}
                style={{
                  borderRadius: 7,
                  border: `1.5px solid ${theme.cardBorder}`,
                  background: theme.bg,
                  color: theme.textMain,
                  padding: "0 10px",
                }}
              >
                <option value="viewer">Viewer</option>
                <option value="editor">Editor</option>
              </select>
            </div>

            <ModalRow style={{ justifyContent: "flex-start", marginBottom: 10 }}>
              <PrimaryButton onClick={findUsersToShare} disabled={shareLoading || !shareUserQuery.trim()}>
                Find
              </PrimaryButton>
            </ModalRow>

            <div style={{ maxHeight: 180, overflowY: "auto", marginBottom: 12 }}>
              {(collaborators || []).length > 0 && (
                <>
                  <div style={{ fontSize: 11, fontWeight: 700, color: theme.textMuted, marginBottom: 6 }}>Current Access</div>
                  {collaborators.map((c) => (
                    <FolderOption key={c.userId} style={{ cursor: "default" }}>
                      <span style={{ fontSize: 12, fontWeight: 700, color: theme.textMain }}>
                        {c.username || c.userId}
                      </span>
                      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                        <span style={{ fontSize: 11, color: theme.textMuted, textTransform: "capitalize" }}>{c.role}</span>
                        <SmallButton
                          $danger
                          onClick={() => handleRemoveCollaborator(c.userId)}
                          disabled={sharingSubmitting}
                        >
                          Remove
                        </SmallButton>
                      </div>
                    </FolderOption>
                  ))}
                </>
              )}

              {shareCandidates.length > 0 && (
                <>
                  <div style={{ fontSize: 11, fontWeight: 700, color: theme.textMuted, margin: "10px 0 6px" }}>Search Results</div>
                  {shareCandidates.map((u) => (
                    <FolderOption key={u._id}>
                      <span style={{ fontSize: 12, fontWeight: 700, color: theme.textMain }}>{u.username}</span>
                      <SmallButton onClick={() => handleShareFolder(u._id)} disabled={sharingSubmitting}>Invite</SmallButton>
                    </FolderOption>
                  ))}
                </>
              )}

              {pendingInvites.length > 0 && (
                <>
                  <div style={{ fontSize: 11, fontWeight: 700, color: theme.textMuted, margin: "10px 0 6px" }}>Pending Invites</div>
                  {pendingInvites.map((p) => (
                    <FolderOption key={p.notificationId} style={{ cursor: "default" }}>
                      <span style={{ fontSize: 12, fontWeight: 700, color: theme.textMain }}>{p.username}</span>
                      <span style={{ fontSize: 11, color: theme.textMuted, textTransform: "capitalize" }}>{p.role}</span>
                    </FolderOption>
                  ))}
                </>
              )}

              {!shareLoading && collaborators.length === 0 && shareCandidates.length === 0 && pendingInvites.length === 0 && (
                <EmptyState style={{ padding: "12px 0", fontSize: 12 }}>No shared users yet.</EmptyState>
              )}
            </div>

            <ModalRow>
              <CancelButton onClick={() => setShareModal(null)}>Close</CancelButton>
            </ModalRow>
          </Modal>
        </Overlay>
      )}
    </Shell>
  );
}
